import { useEffect } from "react";

/**
 * Lightweight SEO manager — updates document head without react-helmet.
 * Call <SEO title=... description=... image=... jsonLd=... /> from any page.
 */
const SITE = "Noxi";
const DEFAULT_DESC = "Noxi — the brutalist freelance marketplace. Hire designers, developers, writers and more. Verified sellers, fair fees, fast delivery.";
const DEFAULT_IMG = "/noxi-logo.png";

function setMeta({ name, property, content }) {
  const key = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(key);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({ title, description, image, type = "website", jsonLd, noindex = false }) {
  useEffect(() => {
    const finalTitle = title ? `${title} · ${SITE}` : `${SITE} — Hire craft. Ship bolder work.`;
    const desc = description || DEFAULT_DESC;
    const img = image || DEFAULT_IMG;
    const url = typeof window !== "undefined" ? window.location.href : "";

    document.title = finalTitle;
    setMeta({ name: "description", content: desc });
    setMeta({ name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" });

    // Open Graph
    setMeta({ property: "og:title", content: finalTitle });
    setMeta({ property: "og:description", content: desc });
    setMeta({ property: "og:image", content: img });
    setMeta({ property: "og:url", content: url });
    setMeta({ property: "og:type", content: type });
    setMeta({ property: "og:site_name", content: SITE });

    // Twitter
    setMeta({ name: "twitter:card", content: "summary_large_image" });
    setMeta({ name: "twitter:title", content: finalTitle });
    setMeta({ name: "twitter:description", content: desc });
    setMeta({ name: "twitter:image", content: img });

    // Canonical
    setLink("canonical", url.split("?")[0]);

    // JSON-LD
    const scriptId = "noxi-jsonld";
    let script = document.getElementById(scriptId);
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, image, type, jsonLd, noindex]);

  return null;
}
